import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ADMIN_USER_FILTERS, type AdminUserRowDto } from '@icebreaker/shared';
import { ERROR_CODES } from '@icebreaker/shared/errors';
import { type TranslationKey, type Translator } from '@icebreaker/shared/i18n';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  Avatar,
  Button,
  EmptyState,
  ErrorBox,
  Input,
  Skeleton,
  Table,
  Tag,
  tableStyles,
  useErrorMessage,
} from '@icebreaker/ui';
import {
  fetchAdminUsers,
  mutationReset,
  suspendUser,
  unsuspendUser,
  usersFilterChanged,
} from '../store/admin.slice';
import { ReasonDialog } from './reason-dialog';
import { UserDetailDialog } from './user-detail-dialog';
import styles from './admin.module.css';

const PAGE_SIZE = 50;

/**
 * Kullanıcı yönetimi (tasarım ekran 25): arama (isim/e-posta/şirket), üç filtre,
 * tablo (avatar monogramlı — kişi görünen yerde monogram zorunlu, README 4.6)
 * ve zorunlu gerekçeli askıya alma.
 *
 * SATIR EYLEMLERİ REFERANSTA İKİDİR: "Detay" (hayalet) + "Askıya al" (çerçeveli
 * yıkıcı). 4. tur bölüm sayımında "Detay" eksikti; katman `UserDetailDialog`.
 */
export function UsersScreen() {
  const t = useTranslation();
  const errorMessage = useErrorMessage();
  const dispatch = useAppDispatch();
  const users = useAppSelector((state: RootState) => state.admin.users);
  const mutation = useAppSelector((state: RootState) => state.admin.mutation);
  const [searchDraft, setSearchDraft] = useState(users.filters.query);
  const [searchParams] = useSearchParams();
  const [dialogTarget, setDialogTarget] = useState<AdminUserRowDto | null>(null);
  const [detailTarget, setDetailTarget] = useState<AdminUserRowDto | null>(null);

  /**
   * ADRESTEN GELEN ARAMA — panelin İÇ KÖPRÜSÜ.
   *
   * E-posta gönderim defteri bir satırı hesaba bağlarken elinde YALNIZ
   * `user_id` olur (adres saklanmıyor, saklanmamalı). Köprü `?q=<uuid>` ile
   * buraya gelir ve sunucu tam kimlik eşleşmesini de arar
   * (`admin-users.repository`). Parametre yoksa hiçbir şey yapılmaz —
   * personelin elle yazdığı arama EZİLMEZ.
   *
   * BİR KEZ UYGULANIR: aynı `q` değeri için tekrar tetiklenmez, yoksa
   * kullanıcı aramayı silip yazdığında adres onu geri yazardı.
   */
  const appliedQueryParam = useRef<string | null>(null);
  const queryParam = searchParams.get('q');
  useEffect(() => {
    if (queryParam === null || appliedQueryParam.current === queryParam) return;
    appliedQueryParam.current = queryParam;
    setSearchDraft(queryParam);
    dispatch(usersFilterChanged({ query: queryParam, filter: 'all', page: 1 }));
  }, [dispatch, queryParam]);

  // Arama girdisi 300 ms bekletilir; filtre değişimi listeyi hemen tazeler.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchDraft !== users.filters.query) {
        dispatch(usersFilterChanged({ query: searchDraft, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [dispatch, searchDraft, users.filters.query]);

  const { query, filter, page } = users.filters;
  const refresh = useCallback(() => {
    dispatch(
      fetchAdminUsers.request({
        query: query || undefined,
        filter,
        page,
        pageSize: PAGE_SIZE,
      }),
    );
  }, [dispatch, query, filter, page]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Çevrimdışı dönüşte otomatik toparlanma: bağlantı geri gelince listeyi tazele.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOnline = () => refresh();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refresh]);

  // Askıya alma başarılı → pencere kapanır (satır slice'ta yerinde güncellendi).
  useEffect(() => {
    if (mutation.phase === 'succeeded' && dialogTarget) {
      setDialogTarget(null);
      dispatch(mutationReset());
    }
  }, [mutation.phase, dialogTarget, dispatch]);

  const suspendMode = dialogTarget?.status !== 'suspended';
  const filtersNarrowed = users.filters.query !== '' || users.filters.filter !== 'all';
  const pageCount = Math.max(1, Math.ceil(users.total / PAGE_SIZE));

  // Başlık sayacı YALNIZ gerçek veriden gelir: ilk yükleme/500 "0 kayıtlı" DEMEZ.
  // Bir kez başarı geldiyse (registeredTotal > 0) tazeleme sırasında son sayı korunur.
  const statsReady = users.phase === 'succeeded' || users.registeredTotal > 0;

  // 429'da sunucunun Retry-After'ı (gövdeye taşınır) varsa süreyi mesaja koy.
  const rateLimitSeconds =
    users.error?.code === ERROR_CODES.RATE_LIMITED &&
    typeof users.error.retryAfterSeconds === 'number'
      ? users.error.retryAfterSeconds
      : null;

  return (
    <section className={styles.screen}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>{t.t('admin.users.title')}</h1>
        {statsReady ? (
          <span className={styles.titleMeta}>
            {t.t('admin.users.headline', {
              total: t.formatNumber(users.registeredTotal),
              recent: t.formatNumber(users.newLast7Days),
            })}
          </span>
        ) : users.phase === 'loading' ? (
          <span className={styles.titleMeta}>{t.t('a11y.loadingContent')}</span>
        ) : null}
      </div>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          placeholder={t.t('admin.users.searchPlaceholder')}
          aria-label={t.t('admin.users.searchLabel')}
        />
        <div
          role="group"
          aria-label={t.t('admin.users.filterLabel')}
          style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--ib-space-3)' }}
        >
          {ADMIN_USER_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={[
                styles.filterBtn,
                users.filters.filter === filter ? styles.filterBtnActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-pressed={users.filters.filter === filter}
              onClick={() => dispatch(usersFilterChanged({ filter, page: 1 }))}
            >
              {t.t(`admin.users.filters.${filter}` as TranslationKey)}
            </button>
          ))}
        </div>
      </div>

      {rateLimitSeconds !== null ? (
        <div className={styles.errorBox} role="alert">
          {t.t('auth.password.rateLimitedSeconds', { count: rateLimitSeconds })}
          <Button variant="secondary" size="sm" onClick={refresh}>
            {t.t('common.retry')}
          </Button>
        </div>
      ) : (
        <ErrorBox error={users.error} className={styles.errorBox}>
          <Button variant="secondary" size="sm" onClick={refresh}>
            {t.t('common.retry')}
          </Button>
        </ErrorBox>
      )}

      <div className={styles.tableWrap}>
        {users.phase === 'loading' && users.items.length === 0 ? (
          <Skeleton label={t.t('a11y.loadingContent')} variant="people" lines={6} />
        ) : users.items.length === 0 && users.phase === 'succeeded' ? (
          <EmptyState
            title={t.t('admin.users.emptyTitle')}
            body={t.t('admin.users.emptyBody')}
            steps={[
              { title: t.t('admin.users.emptyStepSearch') },
              { title: t.t('admin.users.emptyStepFilter') },
            ]}
            /*
              TEK DOLU EYLEM (README 4.7). Yalnız DARALTMA varken çizilir:
              filtresiz boş liste "hiç kullanıcı yok" demektir ve o durumda
              basılacak bir düğme yoktur — uydurma eylem konmaz.
            */
            action={
              filtersNarrowed ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setSearchDraft('');
                    dispatch(usersFilterChanged({ query: '', filter: 'all', page: 1 }));
                  }}
                >
                  {t.t('admin.users.emptyResetCta')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t.t('admin.users.table.user')}</th>
                <th>{t.t('admin.users.table.role')}</th>
                <th>{t.t('admin.users.table.events')}</th>
                <th>{t.t('admin.users.table.wallet')}</th>
                <th>{t.t('admin.users.table.lastSeen')}</th>
                <th>{t.t('admin.users.table.status')}</th>
                <th aria-hidden="true" className={styles.actionsCell} />
              </tr>
            </thead>
            <tbody>
              {users.items.map((user) => (
                <tr key={user.id}>
                  <td>
                    <span className={styles.personCell}>
                      <Avatar name={user.displayName} size="xs" />
                      <span style={{ minWidth: 0 }}>
                        <span className={styles.personName}>{user.displayName}</span>
                        <span className={styles.personMail}>{user.email}</span>
                      </span>
                    </span>
                  </td>
                  <td className={tableStyles.cellMuted}>{roleLabel(t, user)}</td>
                  <td>{t.formatNumber(user.eventCount)}</td>
                  <td>{user.isOrganizer ? '—' : t.formatNumber(user.walletCount)}</td>
                  <td className={styles.cellMeta}>
                    {user.lastSeenAt
                      ? t.formatDate(user.lastSeenAt, { dateStyle: 'medium' })
                      : t.t('admin.users.neverSeen')}
                  </td>
                  <td>
                    <Tag variant={statusVariant(user)}>{statusLabel(t, user)}</Tag>
                  </td>
                  <td className={styles.actionsCell}>
                    <span className={styles.rowActions}>
                      <Button variant="ghost" size="sm" onClick={() => setDetailTarget(user)}>
                        {t.t('admin.users.detail')}
                      </Button>
                      {user.status === 'suspended' ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            dispatch(mutationReset());
                            setDialogTarget(user);
                          }}
                        >
                          {t.t('admin.users.unsuspend')}
                        </Button>
                      ) : (
                        <Button
                          variant="dangerOutline"
                          size="sm"
                          onClick={() => {
                            dispatch(mutationReset());
                            setDialogTarget(user);
                          }}
                        >
                          {t.t('admin.users.suspend')}
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      {/*
        SAYFALAMA — genel görünüm etkinlik tablosuyla aynı desen. Tek sayfada
        GİZLİ (pageCount <= 1); birden çok sayfada Önceki/Sonraki + gösterge.
      */}
      {pageCount > 1 ? (
        <div className={styles.pager}>
          <Button
            variant="secondary"
            size="sm"
            disabled={users.filters.page <= 1}
            onClick={() =>
              dispatch(usersFilterChanged({ page: Math.max(1, users.filters.page - 1) }))
            }
          >
            {t.t('admin.overview.prevPage')}
          </Button>
          <span className={styles.pagerStatus} role="status">
            {t.t('admin.overview.pageStatus', {
              page: t.formatNumber(users.filters.page),
              pageCount: t.formatNumber(pageCount),
            })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!users.hasNext}
            onClick={() => dispatch(usersFilterChanged({ page: users.filters.page + 1 }))}
          >
            {t.t('admin.overview.nextPage')}
          </Button>
        </div>
      ) : null}

      <UserDetailDialog
        user={detailTarget}
        roleLabel={detailTarget ? roleLabel(t, detailTarget) : ''}
        statusLabel={detailTarget ? statusLabel(t, detailTarget) : ''}
        statusVariant={detailTarget ? statusVariant(detailTarget) : 'outline'}
        onClose={() => setDetailTarget(null)}
      />

      <ReasonDialog
        open={dialogTarget !== null}
        tone={suspendMode ? 'negative' : 'positive'}
        title={t.t(suspendMode ? 'admin.users.suspendTitle' : 'admin.users.unsuspendTitle', {
          name: dialogTarget?.displayName ?? '',
        })}
        body={
          <>
            {t.t(suspendMode ? 'admin.users.suspendBody' : 'admin.users.unsuspendBody')}
            {mutation.error ? (
              <span className={styles.errorBox} role="alert" style={{ display: 'block' }}>
                {errorMessage(mutation.error)}
              </span>
            ) : null}
          </>
        }
        confirmLabel={t.t(suspendMode ? 'admin.users.suspend' : 'admin.users.unsuspend')}
        loading={mutation.phase === 'loading'}
        onConfirm={(reason) => {
          if (!dialogTarget) return;
          dispatch(
            suspendMode
              ? suspendUser.request({ userId: dialogTarget.id, reason })
              : unsuspendUser.request({ userId: dialogTarget.id, reason }),
          );
        }}
        onCancel={() => {
          setDialogTarget(null);
          dispatch(mutationReset());
        }}
      />
    </section>
  );
}

/**
 * Rol sütunu — YALNIZ TÜRETİLMİŞ roller ("Katılımcı · Konuşmacı" deseni).
 *
 * `users.role` KALDIRILDI (QA S2/S3 · P3-1): S3'ten sonra o sütun hiçbir yetki
 * türetmiyor, hiçbir uç onu değiştirmiyor ve tüm satırlar `'user'`. Panel yine
 * de "Yönetici" yazıyordu ve operatör bunu "bu kullanıcı yönetici" diye okuyor,
 * yani YANLIŞ bir olgu görüyordu. Kalan iki etiket (organizatör / konuşmacı)
 * gerçek ve doğrulanabilir olgulardır — `is_organizer` ve `is_speaker`.
 *
 * PERSONEL ROLÜ BURADA GÖSTERİLEMEZ: `users` ile `staff_accounts` arasında bağ
 * yoktur (Karar 2a). Personel rolü panelin PERSONEL ekranına aittir.
 */
function roleLabel(t: Translator, user: AdminUserRowDto): string {
  const parts = [
    user.isOrganizer ? t.t('admin.users.roles.organizer') : t.t('admin.users.roles.user'),
  ];
  if (user.isSpeaker) parts.push(t.t('admin.users.roles.speaker'));
  return parts.join(' · ');
}

function statusLabel(t: Translator, user: AdminUserRowDto): string {
  if (user.status === 'suspended') return t.t('admin.users.statuses.suspended');
  if (user.flagged) return t.t('admin.users.statuses.flagged');
  return t.t(`admin.users.statuses.${user.status}` as TranslationKey);
}

function statusVariant(user: AdminUserRowDto): 'outline' | 'danger' | 'warning' {
  if (user.status === 'suspended') return 'danger';
  if (user.flagged) return 'warning';
  return 'outline';
}
